import React from "react";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCertificate, faCheck, faEllipsis } from "@fortawesome/free-solid-svg-icons";
import { usePortfolioData } from "../contexts/DataContext";
import "./styles/ExperienceSkillsSection.css";

const fadeUp = {
	hidden: { opacity: 0, y: 20 },
	visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const stagger = {
	hidden: {},
	visible: { transition: { staggerChildren: 0.06 } },
};

const child = {
	hidden: { opacity: 0, y: 10 },
	visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
};

const ExperienceSkillsSection = () => {
	const { data: INFO } = usePortfolioData();
	const { skills, experience, certifications } = INFO;
	const skillCategories = Object.entries(skills).filter(([k]) => k !== "concepts");

	return (
		<section className="exp">
			<div className="content-wrapper">
				<motion.div
					className="exp__header"
					variants={fadeUp}
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true }}
				>
					<h1 className="exp__title">What I do<br />&amp; what I know</h1>
					<p className="exp__subtitle">Currently at Elevance Health</p>
				</motion.div>

				{/* Work */}
				<div className="exp__work">
					{experience.map((job, i) => (
						<motion.div
							key={i}
							className="exp__job"
							variants={fadeUp}
							initial="hidden"
							whileInView="visible"
							viewport={{ once: true }}
						>
							<div className="exp__job-left">
								<span className="exp__job-duration">{job.duration}</span>
							</div>
							<div className="exp__job-right">
								<h3 className="exp__job-role">{job.role}</h3>
								<p className="exp__job-company">{job.company}</p>
								<p className="exp__job-desc">{job.description}</p>
								{job.environment && (
									<div className="exp__job-env">
										{job.environment.map((t) => (
											<span key={t}>{t}</span>
										))}
									</div>
								)}
							</div>
						</motion.div>
					))}
				</div>

				{/* Skills */}
				<motion.div
					className="exp__skills"
					variants={stagger}
					initial="hidden"
					whileInView="visible"
					viewport={{ once: true, amount: 0.1 }}
				>
					<h2 className="exp__section-heading">What I work with</h2>
					<div className="exp__skills-columns">
						{skillCategories.map(([category, items]) => (
							<div key={category} className="exp__skill-group">
								<h4 className="exp__skill-category">{category}</h4>
								{items.map((skill, j) => (
									<motion.div key={j} className="exp__skill-row" variants={child}>
										<span className="exp__skill-name">{skill.name}</span>
										<span className="exp__skill-years">{skill.level}</span>
									</motion.div>
								))}
							</div>
						))}
					</div>

					{skills.concepts && (
						<div className="exp__concepts">
							<h4 className="exp__skill-category">Standards I follow</h4>
							<p className="exp__concepts-list">
								{skills.concepts.join("  ·  ")}
							</p>
						</div>
					)}
				</motion.div>

				{/* Certifications */}
				{certifications && certifications.length > 0 && (
					<motion.div
						className="exp__certs"
						variants={stagger}
						initial="hidden"
						whileInView="visible"
						viewport={{ once: true }}
					>
						<h2 className="exp__section-heading">Certs & training</h2>
						{certifications.map((cert, i) => (
							<motion.div key={i} className="exp__cert" variants={child}>
								<FontAwesomeIcon
									icon={faCertificate}
									className={`exp__cert-icon ${cert.status === "Completed" ? "exp__cert-icon--done" : ""}`}
								/>
								<div className="exp__cert-info">
									<span className="exp__cert-name">{cert.name}</span>
									<span className={`exp__cert-status ${cert.status === "Completed" ? "exp__cert-status--done" : ""}`}>
										<FontAwesomeIcon icon={cert.status === "Completed" ? faCheck : faEllipsis} />
										{cert.status}
									</span>
								</div>
								{cert.details && (
									<ul className="exp__cert-details">
										{cert.details.map((d, j) => <li key={j}>{d}</li>)}
									</ul>
								)}
							</motion.div>
						))}
					</motion.div>
				)}
			</div>
		</section>
	);
};

export default ExperienceSkillsSection;
